<script lang="ts" setup>
import LabelText from '~/components/form-elements/LabelText.vue'
import type { AutocompleteItem, SelectedItem } from '~/types/form-elements'

const model = defineModel<string | null>()

const props = withDefaults(
  defineProps<{
    autocompleteItems: AutocompleteItem[]
    isLabelTextWhite?: boolean
    isRequired?: boolean
    labelText?: string | null
    lightGreenBorder?: boolean
    placeholder?: string
    selectedItem?: SelectedItem | null
    showArrow?: boolean
  }>(),
  {
    isLabelTextWhite: false,
    isRequired: false,
    labelText: null,
    lightGreenBorder: false,
    placeholder: undefined,
    selectedItem: null,
    showArrow: false
  }
)

const emit = defineEmits<{
  (e: 'updateSelectedItem', x: SelectedItem | null, isAdding: boolean): void
}>()

const selectedItemCopy = ref<SelectedItem | null>(null)
const showAutocompleteItems = ref(false)

watch(
  () => props.selectedItem,
  (): void => {
    initSelectedItemCopy()
  }
)

function addSelectedItem (id: string | number): void {
  const autocompleteItem = props.autocompleteItems.find((x: AutocompleteItem): boolean => x.id === id)

  if (autocompleteItem && (selectedItemCopy.value === null || autocompleteItem.id !== selectedItemCopy.value.id)) {
    model.value = autocompleteItem.name
    selectedItemCopy.value = autocompleteItem

    updateSelectedItem(true)
  }
}

function deleteSelectedItem (): void {
  selectedItemCopy.value = null
  model.value = null

  updateSelectedItem(false)
}

function clearInput (): void {
  showAutocompleteItems.value = false
  model.value = selectedItemCopy.value === null ? null : selectedItemCopy.value.name
}

function initSelectedItemCopy (): void {
  selectedItemCopy.value = props.selectedItem
  model.value = selectedItemCopy.value === null ? null : selectedItemCopy.value.name
}

function isItemSelected (id: string | number): boolean {
  return selectedItemCopy.value !== null && selectedItemCopy.value.id === id
}

function updateSelectedItem (isAdding: boolean): void {
  emit('updateSelectedItem', selectedItemCopy.value, isAdding)
}

initSelectedItemCopy()
</script>

<template>
  <div class="single-select">
    <LabelText
      class="mb-1"
      :class="{ 'single-select__label-text_white': isLabelTextWhite }"
      :is-required="isRequired"
      :value="labelText"
    />

    <div
      class="input-area flex items-center bg-white"
      :class="{
        'has-autocomplete': showAutocompleteItems && autocompleteItems.length,
        'light-green-border': lightGreenBorder
      }"
    >
      <input
        v-model="model"
        autocomplete="off"
        :placeholder="placeholder"
        @blur="clearInput"
        @focus="showAutocompleteItems = true"
      >

      <SvgIcon
        v-if="selectedItemCopy"
        class="close-icon cursor-pointer"
        name="outlined/close"
        @click="deleteSelectedItem"
      />

      <SvgIcon
        v-if="showArrow"
        class="cursor-pointer"
        :name="showAutocompleteItems ? 'outlined/expand-less' : 'outlined/expand-more'"
        @click="showAutocompleteItems = !showAutocompleteItems"
      />
    </div>

    <ul
      v-show="showAutocompleteItems && autocompleteItems.length"
      class="autocomplete-items bg-white"
      :class="{ 'light-green-border': lightGreenBorder }"
    >
      <li
        v-for="x in autocompleteItems"
        :key="x.id"
        class="autocomplete-item cursor-pointer"
        :class="{ 'is-selected': isItemSelected(x.id) }"
        @mousedown="addSelectedItem(x.id)"
      >
        <a v-if="x.path" :href="x.path">
          {{ x.name }}
        </a>
        <template v-else>
          {{ x.name }}
        </template>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@use 'assets/css/autocomplete';
@use 'assets/css/variables';

.single-select {
  position: relative;

  &__label-text_white {
    color: white;
  }
}

.input-area {
  border: 1px solid color.mix(variables.$main-gray-color, white, 60%);
  border-radius: 12px;
  box-sizing: border-box;
  overflow: hidden;

  &.has-autocomplete {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.light-green-border {
    border-color: variables.$main-green-color;
  }

  input {
    appearance: none;
    border: none;
    font-size: 16px;
    outline: none;
    line-height: 24px;
    padding: 11px 15px;
    width: 100%;

    @media screen and (max-width: 480px) {
      padding: 7px 11px;
    }
  }

  svg {
    font-size: 24px;
    margin: 0 16px;
    user-select: none;

    &.icon-close:hover {
      color: variables.$main-red-color;
    }
  }
}
</style>
